import React from "react";
import ServiceLandingPage, {
  InternalLink,
} from "../components/ServiceLandingPage";
import { COMPANY } from "../content";

const SCOPE = [
  {
    name: "Office strip-out & shell-and-core fit-out",
    desc: "Full demolition, partition walls, raised floors, suspended ceilings and shell-and-core conversion for new tenancies — across Trakheesi, JAFZA, DMCC, DIFC, Dubai South and free zones.",
  },
  {
    name: "MEP coordination & smart workplace",
    desc: "Lighting design, AC re-zoning, electrical and data cabling, fire-life-safety integration, AV and smart-meeting-room infrastructure with consultant sign-off.",
  },
  {
    name: "Workstations, joinery & boardroom furniture",
    desc: "Custom workstations, executive desks, boardroom tables, reception counters, pantry joinery and storage walls manufactured at our in-house Sharjah atelier.",
  },
  {
    name: "Glass partitions, doors & branding",
    desc: "Frameless glass partitions, double-glazed acoustic walls, branded reception walls, signage, vinyl manifestation and back-painted glass features.",
  },
  {
    name: "Flooring, ceilings & acoustic finishes",
    desc: "Carpet tiles, vinyl, engineered wood, microcement, acoustic baffles, and metal/gypsum ceilings — to corporate and consultant specifications.",
  },
  {
    name: "Office furniture, breakout & styling",
    desc: "Lounge furniture, breakout pods, biophilic walls, art curation and final styling — so the office is photo-ready on handover, not just functional.",
  },
];

const PROCESS = [
  {
    n: "01",
    title: "Site survey & space planning",
    body: "We visit the office, review consultant drawings, take measurements and align on workstation count, departments, meeting rooms and breakout zones.",
  },
  {
    n: "02",
    title: "Design, BOQ & timeline",
    body: "3D layout, finish boards, FF&E selection and an itemised BOQ — built around your move-in deadline and budget envelope.",
  },
  {
    n: "03",
    title: "Approvals & landlord coordination",
    body: "Trakheesi, free-zone authority (DMCC, JAFZA, DIFC, Dubai South), Dubai Civil Defence, building management deposits and access scheduling — all handled by us.",
  },
  {
    n: "04",
    title: "Phased on-site execution",
    body: "Strip-out → MEP first-fix → ceilings → partitions → joinery → flooring → second-fix → IT/AV commissioning — coordinated by a single project lead.",
  },
  {
    n: "05",
    title: "Snag, commissioning & handover",
    body: "Snag list, deep clean, IT/AV commissioning, defects-warranty period and a styled walkthrough — your team can move in on day one.",
  },
];

const FAQS = [
  {
    q: "How long does an office fit-out take in Dubai?",
    a: "A typical 5,000–10,000 sq.ft commercial office fit-out in Dubai takes 8–14 weeks of on-site execution, plus 2–4 weeks for design, BOQ, FF&E selection and authority approvals. Larger HQ fit-outs (20,000+ sq.ft) typically run 16–24 weeks.",
  },
  {
    q: "How much does office fit-out cost in Dubai?",
    a: "Office fit-out cost in Dubai typically ranges from AED 180 to AED 600 per sq.ft depending on category — Cat A (shell improvements), Cat B (full corporate fit-out) or Premium (executive HQ with bespoke joinery, marble and AV). Time Furniture provides itemised quotations after a free site survey.",
  },
  {
    q: "Do you handle Trakheesi and free-zone approvals?",
    a: "Yes. We coordinate fit-out approvals across Trakheesi (DM), Dubai Civil Defence, DMCC, JAFZA, DIFC, Dubai South, Dubai Internet/Media City, ADGM and other UAE free zones — including consultant drawings, NOCs, deposits and access permits — as part of the contract.",
  },
  {
    q: "Can you fit-out an occupied office without disrupting work?",
    a: "Yes. For occupied or phased fit-outs we plan night and weekend works, dust barriers, temporary partitions and zone-by-zone handovers so business continuity isn't disrupted. We've delivered this model for clinics, banks and corporate tenants across DIFC and DMCC.",
  },
  {
    q: "Do you also supply office furniture, AV and IT cabling?",
    a: "Yes. Time Furniture is a turnkey contractor — we deliver custom workstations, boardroom tables, breakout furniture and acoustic pods from our Sharjah atelier, and coordinate IT cabling, AV systems and smart-meeting-room infrastructure as part of the same contract.",
  },
];

const WHY = [
  {
    t: "Trakheesi & free-zone fluency",
    d: "Three decades of approvals across DM, DCD, DMCC, JAFZA, DIFC and Dubai South — we know what each authority asks for, before they ask.",
  },
  {
    t: "Single-contract delivery",
    d: "Civil, MEP, joinery, AV, IT, furniture and styling under one accountable team — your facilities lead deals with one person, not a dozen.",
  },
  {
    t: "On-time, on-budget",
    d: "Itemised BOQs, fixed handover dates and weekly progress reporting — so finance and HR can plan the move-in with confidence.",
  },
];

export default function OfficeFitOutDubai() {
  return (
    <ServiceLandingPage
      testid="office-fit-out-dubai-page"
      whatsappText={COMPANY.whatsappOfficeText}
      pageTitle="Office Fit-Out Dubai | Commercial Interior Fit-Out Contractor — Time Furniture"
      metaDescription="Turnkey office fit-out contractor in Dubai. Trakheesi, DMCC, JAFZA, DIFC and free-zone approvals, MEP, joinery, AV and corporate furniture under one team. UAE since 1993."
      canonical="https://timefurniture.ae/office-fit-out-dubai"
      serviceSchemaName="Office and Commercial Interior Fit-Out — Dubai & UAE"
      breadcrumbLabel="Office Fit-Out Dubai"
      eyebrow="Office & Commercial Fit-Out · Dubai"
      h1="Office Fit-Out Contractor in"
      h1Accent="Dubai & UAE"
      intro="Time Furniture is a turnkey office fit-out contractor in Dubai delivering single-contract commercial interiors for corporate tenants, free-zone businesses, clinics and retail. Since 1993, we've handed over offices, headquarters and showrooms across DIFC, DMCC, JAFZA, Business Bay, Dubai Internet City, Dubai South, ADGM and the wider UAE — managing Trakheesi and authority approvals, MEP, joinery, glass partitions, AV and IT, custom workstations and final styling. Whether you're a 50-desk free-zone tenant moving into a Cat-A shell, a clinic needing DCD-compliant approvals, or a regional HQ requiring premium executive fit-out, we deliver a transparent BOQ, a fixed handover date and a fully commissioned, move-in-ready office on day one."
      scope={SCOPE}
      process={PROCESS}
      whyChoose={WHY}
      faqs={FAQS}
      ctaHeading="Planning your"
      ctaAccent="office fit-out"
      ctaSubtext="Book a free site survey. We'll review your shell, propose a fit-out plan and reply with a transparent BOQ tied to your move-in deadline."
      internalLinksLine={
        <>
          Also see our{" "}
          <InternalLink to="/services">full fit-out services</InternalLink>
          ,{" "}
          <InternalLink to="/portfolio">completed commercial projects</InternalLink>
          , or our{" "}
          <InternalLink to="/turnkey-contractor-uae">turnkey contractor in the UAE</InternalLink>{" "}
          page.
        </>
      }
    />
  );
}
